/**
 * ERP & Treuhandbüro Data Portability / Connector Module (`web/src/lib/store.tsx`).
 *
 * @statutory OR Art. 957a Abs. 2 Ziff. 1–5 (Vollständige & nachprüfbare Buchführung) · GeBüV Art. 6–10 (Datenübernahme & Prüfspur) · MWSTG Art. 70 · Schweizer Kontenrahmen KMU
 */
import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { api } from './api';

export interface TenantSummary { id: string; name: string; isDemo: boolean; status: 'setup' | 'live'; legalForm: string; city: string; canton: string; vatMethod: string; autonomyLevel: number; cash: number; result: number; pending: number; questions: number; unmatched: number; autoRate: number; nextDeadline: { date: string; title: string } | null }
interface Toast { id: number; text: string; kind?: 'error' }
export interface CommandSelection { type: 'proposal' | 'entry' | 'account'; id: string; label: string }
export interface CommandInfo { kind: string; title: string; icon: string; group: string; template: string; examples: string[] }
/** A request for the Command panel: prefill (caret at «…») or send right away. */
export interface CommandRequest { text: string; send: boolean; n: number }
interface AppState {
  tenants: TenantSummary[]; tenantsLoaded: boolean; refreshTenants: () => void;
  health: { mode: 'claude' | 'offline'; models: Record<string, string> | null; today: string } | null;
  toast: (text: string, kind?: 'error') => void;
  version: number; bump: () => void;
  openPalette: (query?: string) => void; paletteOpen: boolean; closePalette: () => void; paletteQuery: string;
  agentDraft: string | null; setAgentDraft: (s: string | null) => void;
  commandOpen: boolean; openCommand: (text?: string, send?: boolean) => void; closeCommand: () => void;
  commandRequest: CommandRequest | null; commandFocus: number;
  commandSelection: CommandSelection | null; setCommandSelection: (s: CommandSelection | null) => void;
  commandRegistry: CommandInfo[];
}
const Ctx = createContext<AppState>(null!);
export const useApp = () => useContext(Ctx);

export function AppProvider({ children }: { children: ReactNode }) {
  const [tenants, setTenants] = useState<TenantSummary[]>([]);
  const [tenantsLoaded, setTenantsLoaded] = useState(false);
  const [health, setHealth] = useState<AppState['health']>(null);
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [version, setVersion] = useState(0);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [paletteQuery, setPaletteQuery] = useState('');
  const [agentDraft, setAgentDraft] = useState<string | null>(null);
  const [commandOpen, setCommandOpen] = useState(() => { try { return localStorage.getItem('kontor-command') === 'open' && matchMedia('(min-width: 1200px)').matches; } catch { return false; } });
  const [commandRequest, setCommandRequest] = useState<CommandRequest | null>(null);
  const [commandFocus, setCommandFocus] = useState(0);
  const [commandSelection, setCommandSelection] = useState<CommandSelection | null>(null);
  const [commandRegistry, setCommandRegistry] = useState<CommandInfo[]>([]);
  useEffect(() => { api.get<CommandInfo[]>('/api/commands').then(setCommandRegistry).catch(() => {}); }, []);
  useEffect(() => { try { localStorage.setItem('kontor-command', commandOpen ? 'open' : 'closed'); } catch { /* private mode */ } document.documentElement.dataset.command = commandOpen ? 'open' : 'closed'; }, [commandOpen]);
  const tid = useRef(0);
  const refreshTenants = useCallback(() => { api.get<TenantSummary[]>('/api/tenants').then((t) => { setTenants(t); setTenantsLoaded(true); }).catch(() => {}); }, []);
  useEffect(() => { refreshTenants(); api.get<any>('/api/health').then(setHealth).catch(() => {}); }, [refreshTenants, version]);
  const toast = useCallback((text: string, kind?: 'error') => {
    const id = ++tid.current;
    setToasts((t) => [...t, { id, text, kind }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), kind === 'error' ? 6000 : 3200);
  }, []);
  const value: AppState = {
    tenants, tenantsLoaded, refreshTenants, health, toast, version, bump: () => setVersion((v) => v + 1),
    openPalette: (q = '') => { setPaletteQuery(q); setPaletteOpen(true); }, paletteOpen, closePalette: () => setPaletteOpen(false), paletteQuery,
    agentDraft, setAgentDraft,
    commandOpen, commandRequest, commandFocus, commandSelection, setCommandSelection, commandRegistry,
    openCommand: (text, send = false) => { setCommandOpen(true); setCommandFocus((n) => n + 1); if (text != null) setCommandRequest({ text, send, n: Date.now() }); },
    closeCommand: () => setCommandOpen(false),
  };
  return (
    <Ctx.Provider value={value}>
      {children}
      <div className="toast-stack" role="status" aria-live="polite">
        {toasts.map((t) => <div key={t.id} className={`toast ${t.kind ?? ''}`}>{t.text}</div>)}
      </div>
    </Ctx.Provider>
  );
}
